import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../../base/avatar/components/Avatar';
import { IconRemoteControlStop } from '../../../base/icons/svg';
import { getParticipantDisplayName } from '../../../base/participants/functions';
import Button from '../../../base/ui/components/web/Button';
import Input from '../../../base/ui/components/web/Input';
import { BUTTON_TYPES } from '../../../base/ui/constants.web';
import { sendControllerCombo, sendControllerText } from '../../actions';
import { KEY_COMBOS } from '../../constants';
const useStyles = makeStyles()(theme => {
    return {
        bar: {
            // Fixed rather than positioned inside the video area: the filmstrip
            // and the toolbar both sit at the bottom of that area and both draw
            // above it, and the one control that ends this must never end up
            // behind anything.
            position: 'fixed',
            bottom: '104px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 252,
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            flexWrap: 'wrap',
            justifyContent: 'center',
            maxWidth: 'calc(100vw - 32px)',
            padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
            borderRadius: '999px',
            background: `${theme.palette.ui01}F2`,
            backdropFilter: 'blur(16px)',
            boxShadow: '0 12px 40px -8px rgba(0, 0, 0, .6)',
            border: `1px solid ${theme.palette.ui03}55`
        },
        identity: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            paddingLeft: theme.spacing(1),
            minWidth: 0
        },
        state: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0
        },
        stateTitle: {
            ...theme.typography.bodyShortBold,
            color: theme.palette.text01,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
        },
        stateHint: {
            ...theme.typography.labelRegular,
            color: theme.palette.text02,
            whiteSpace: 'nowrap'
        },
        divider: {
            width: '1px',
            alignSelf: 'stretch',
            margin: `${theme.spacing(1)} 0`,
            background: `${theme.palette.ui03}55`
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1)
        },
        keys: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            flexWrap: 'wrap'
        },
        key: {
            ...theme.typography.labelBold,
            color: theme.palette.text01,
            background: `${theme.palette.ui03}44`,
            border: `1px solid ${theme.palette.ui03}66`,
            borderRadius: '6px',
            padding: '4px 10px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'background .12s ease-out, border-color .12s ease-out',
            '&:hover': {
                background: `${theme.palette.ui03}77`,
                borderColor: theme.palette.action01
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.action01}`,
                outlineOffset: '2px'
            }
        },
        typeRow: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            width: '260px'
        },
        panel: {
            position: 'absolute',
            bottom: 'calc(100% + 12px)',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2),
            maxWidth: 'min(420px, calc(100vw - 32px))',
            padding: theme.spacing(3),
            borderRadius: '12px',
            background: `${theme.palette.ui01}F2`,
            backdropFilter: 'blur(16px)',
            boxShadow: '0 12px 40px -8px rgba(0, 0, 0, .6)',
            border: `1px solid ${theme.palette.ui03}55`
        },
        panelTitle: {
            ...theme.typography.labelRegular,
            color: theme.palette.text02
        }
    };
});
/**
 * The controls available to whoever is driving someone else's computer.
 *
 * Two ways out sit side by side because they are genuinely different: letting
 * go of the keyboard, and ending the session. Confusing them would either trap
 * a controller's keystrokes or hang up on the person who granted access.
 *
 * @param {IProps} props - The properties.
 * @returns {ReactElement}
 */
const ControllerBar = ({ engaged, onRelease, onStop, paused }) => {
    const { classes } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [panel, setPanel] = useState();
    const [text, setText] = useState('');
    const controlled = useSelector((state) => state['features/remote-control'].controller.controlled);
    const controlledName = useSelector((state) => (controlled ? getParticipantDisplayName(state, controlled) : ''));
    let hint = t('remoteControl.engageHint');
    if (paused) {
        hint = t('remoteControl.pausedHint');
    }
    else if (engaged) {
        hint = t('remoteControl.releaseHint');
    }
    const togglePanel = useCallback((which) => () => {
        setPanel(current => (current === which ? undefined : which));
    }, []);
    const onSendCombo = useCallback((modifiers, key) => () => {
        dispatch(sendControllerCombo(modifiers, key));
    }, [dispatch]);
    const onSendText = useCallback(() => {
        dispatch(sendControllerText(text));
        setText('');
        setPanel(undefined);
    }, [dispatch, text]);
    return (React.createElement("div", { className: classes.bar },
        React.createElement("div", { className: classes.identity },
            React.createElement(Avatar, { participantId: controlled, size: 32 }),
            React.createElement("div", { className: classes.state },
                React.createElement("span", { className: classes.stateTitle }, engaged
                    ? t('remoteControl.controllingScreen', { user: controlledName })
                    : t('remoteControl.canControlScreen', { user: controlledName })),
                React.createElement("span", { className: classes.stateHint }, hint))),
        React.createElement("div", { className: classes.divider }),
        React.createElement("div", { className: classes.actions },
            React.createElement(Button, { accessibilityLabel: t('remoteControl.sendKeys'), disabled: paused, labelKey: 'remoteControl.sendKeys', onClick: togglePanel('keys'), size: 'small', type: BUTTON_TYPES.SECONDARY }),
            React.createElement(Button, { accessibilityLabel: t('remoteControl.typeText'), disabled: paused, labelKey: 'remoteControl.typeText', onClick: togglePanel('text'), size: 'small', type: BUTTON_TYPES.SECONDARY }),
            engaged && !paused && (React.createElement(Button, { accessibilityLabel: t('remoteControl.release'), labelKey: 'remoteControl.release', onClick: onRelease, size: 'small', type: BUTTON_TYPES.SECONDARY })),
            React.createElement(Button, { accessibilityLabel: t('remoteControl.endControl'), icon: IconRemoteControlStop, labelKey: 'remoteControl.endControl', onClick: onStop, size: 'small', type: BUTTON_TYPES.DESTRUCTIVE })),
        panel === 'keys' && (React.createElement("div", { className: classes.panel },
            React.createElement("span", { className: classes.panelTitle }, t('remoteControl.sendKeysDescription')),
            React.createElement("div", { className: classes.keys }, KEY_COMBOS.map(combo => (React.createElement("button", { className: classes.key, key: combo.id, onClick: onSendCombo(combo.modifiers, combo.key), type: 'button' }, t(`remoteControl.combo.${combo.id}`))))))),
        panel === 'text' && (React.createElement("div", { className: classes.panel },
            React.createElement("span", { className: classes.panelTitle }, t('remoteControl.typeTextDescription')),
            React.createElement("div", { className: classes.typeRow },
                React.createElement(Input, { id: 'remote-control-type-text', onChange: setText, placeholder: t('remoteControl.typeTextPlaceholder'), value: text }),
                React.createElement(Button, { accessibilityLabel: t('remoteControl.send'), disabled: !text, labelKey: 'remoteControl.send', onClick: onSendText, size: 'small' }))))));
};
export default ControllerBar;
